<script setup>
import { ref, computed, onMounted } from "vue";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import GradientLineChart from "@/quote/Charts/GradientLineChart.vue";

const cargando = ref(true);
const egresos = ref([]);
const ingresos = ref([]);
const resumen = ref({ ingresosPorMes: [], egresosPorMes: [] });
const pnl = ref(null);

const showModal = ref(false);
const guardando = ref(false);
const form = ref({ category: "", amount: "", expenseDate: "", description: "" });

const showModalIngreso = ref(false);
const guardandoIngreso = ref(false);
const formIngreso = ref({ category: "", amount: "", incomeDate: "", description: "" });

const rango = ref({
  from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().substring(0, 10),
  to: new Date().toISOString().substring(0, 10)
});

const CATEGORIAS = ["Proveedores", "Nómina", "Arriendo", "Servicios públicos", "Marketing", "Impuestos", "Otro"];
const CATEGORIAS_INGRESO = ["Comisión", "Venta directa", "Intereses", "Reembolso", "Otro"];

async function getEgresos() {
  const res = await api.get("/accounting/egresos");
  egresos.value = res.data.egresos;
}

async function getIngresos() {
  const res = await api.get("/accounting/ingresos");
  ingresos.value = res.data.ingresos;
}

async function getResumen() {
  const res = await api.get("/accounting/summary");
  resumen.value = res.data;
}

async function getPnl() {
  const res = await api.get("/accounting/pnl", { params: rango.value });
  pnl.value = res.data.pnl;
}

function abrirCrear() {
  form.value = { category: "", amount: "", expenseDate: "", description: "" };
  showModal.value = true;
}

async function guardarEgreso() {
  if (!form.value.category || !form.value.amount) {
    Swal.fire({ icon: "warning", title: "Categoría y monto son obligatorios" });
    return;
  }

  guardando.value = true;

  try {
    await api.post("/accounting/egresos", form.value);
    showModal.value = false;
    await Promise.all([getEgresos(), getResumen(), getPnl()]);
    Swal.fire({ icon: "success", title: "Egreso registrado", timer: 1500, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo registrar el egreso", text: err.response?.data?.error || "Error de conexión." });
  } finally {
    guardando.value = false;
  }
}

async function eliminarEgreso(egreso) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar este egreso?",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  await api.delete(`/accounting/egresos/${egreso.id}`);
  await Promise.all([getEgresos(), getResumen(), getPnl()]);
}

function abrirCrearIngreso() {
  formIngreso.value = { category: "", amount: "", incomeDate: "", description: "" };
  showModalIngreso.value = true;
}

async function guardarIngreso() {
  if (!formIngreso.value.category || !formIngreso.value.amount) {
    Swal.fire({ icon: "warning", title: "Categoría y monto son obligatorios" });
    return;
  }

  guardandoIngreso.value = true;

  try {
    await api.post("/accounting/ingresos", formIngreso.value);
    showModalIngreso.value = false;
    await Promise.all([getIngresos(), getResumen(), getPnl()]);
    Swal.fire({ icon: "success", title: "Ingreso registrado", timer: 1500, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo registrar el ingreso", text: err.response?.data?.error || "Error de conexión." });
  } finally {
    guardandoIngreso.value = false;
  }
}

async function eliminarIngreso(ingreso) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar este ingreso?",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  await api.delete(`/accounting/ingresos/${ingreso.id}`);
  await Promise.all([getIngresos(), getResumen(), getPnl()]);
}

function formatCOP(valor) {
  return Number(valor || 0).toLocaleString("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 });
}

const chartData = computed(() => ({
  labels: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
  datasets: [
    { label: "Ingresos", data: resumen.value.ingresosPorMes.map(m => m.total) },
    { label: "Egresos", data: resumen.value.egresosPorMes.map(m => m.total) }
  ]
}));

onMounted(async () => {
  cargando.value = true;
  await Promise.all([getEgresos(), getIngresos(), getResumen(), getPnl()]);
  cargando.value = false;
});
</script>

<template>
  <div class="container-fluid py-4">

    <h4 class="mb-3" style="color: white;"><i class="bi bi-calculator me-2"></i>Contabilidad</h4>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <template v-else>

      <!-- RESUMEN / P&L -->
      <div class="row g-3 mb-3">
        <div class="col-12">
          <gradient-line-chart id="chart-ingresos-egresos" title="Ingresos vs Egresos (año en curso)" :chart="chartData" />
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 class="mb-0">Pérdidas y ganancias</h6>
          <div class="d-flex gap-2 align-items-center">
            <input v-model="rango.from" type="date" class="form-control form-control-sm" @change="getPnl">
            <span>a</span>
            <input v-model="rango.to" type="date" class="form-control form-control-sm" @change="getPnl">
          </div>
        </div>
        <div v-if="pnl" class="card-body">
          <div class="row text-center">
            <div class="col-md-4">
              <p class="text-muted small mb-0">Ingresos</p>
              <h5 class="text-success">{{ formatCOP(pnl.ingresos) }}</h5>
            </div>
            <div class="col-md-4">
              <p class="text-muted small mb-0">Egresos</p>
              <h5 class="text-danger">{{ formatCOP(pnl.egresos) }}</h5>
            </div>
            <div class="col-md-4">
              <p class="text-muted small mb-0">Utilidad</p>
              <h5 :class="pnl.utilidad >= 0 ? 'text-success' : 'text-danger'">{{ formatCOP(pnl.utilidad) }}</h5>
            </div>
          </div>

          <hr>
          <div class="row">
            <div class="col-md-6">
              <h6 class="small text-muted">Ingresos por categoría</h6>
              <div v-for="cat in pnl.ingresosPorCategoria" :key="cat.category" class="d-flex justify-content-between">
                <span>{{ cat.category }}</span>
                <span>{{ formatCOP(cat.total) }}</span>
              </div>
              <div v-if="pnl.ingresosPorCategoria.length === 0" class="text-muted small">Sin ingresos en este rango.</div>
            </div>
            <div class="col-md-6">
              <h6 class="small text-muted">Egresos por categoría</h6>
              <div v-for="cat in pnl.egresosPorCategoria" :key="cat.category" class="d-flex justify-content-between">
                <span>{{ cat.category }}</span>
                <span>{{ formatCOP(cat.total) }}</span>
              </div>
              <div v-if="pnl.egresosPorCategoria.length === 0" class="text-muted small">Sin egresos en este rango.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- INGRESOS -->
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Ingresos manuales</h6>
          <argon-button color="success" size="sm" @click="abrirCrearIngreso">
            <i class="bi bi-plus-circle me-1"></i>Nuevo ingreso
          </argon-button>
        </div>
        <div class="card-body table-responsive">
          <p class="text-muted small">
            Los pagos/abonos registrados en Reservas ya cuentan como ingreso automáticamente -- acá solo cargás ingresos que no vienen de una reserva puntual.
          </p>
          <table class="table table-striped align-middle">
            <thead>
              <tr><th>Fecha</th><th>Categoría</th><th>Descripción</th><th>Monto</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="i in ingresos" :key="i.id">
                <td>{{ new Date(i.income_date).toLocaleDateString("es-CO") }}</td>
                <td>{{ i.category }}</td>
                <td>{{ i.description || "—" }}</td>
                <td>{{ formatCOP(i.amount) }}</td>
                <td><button class="btn btn-sm btn-outline-danger" @click="eliminarIngreso(i)"><i class="bi bi-trash"></i></button></td>
              </tr>
              <tr v-if="ingresos.length === 0">
                <td colspan="5" class="text-center text-muted py-4">Sin ingresos manuales registrados.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- EGRESOS -->
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Egresos</h6>
          <argon-button color="success" size="sm" @click="abrirCrear">
            <i class="bi bi-plus-circle me-1"></i>Nuevo egreso
          </argon-button>
        </div>
        <div class="card-body table-responsive">
          <table class="table table-striped align-middle">
            <thead>
              <tr><th>Fecha</th><th>Categoría</th><th>Descripción</th><th>Proveedor</th><th>Monto</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="e in egresos" :key="e.id">
                <td>{{ new Date(e.expense_date).toLocaleDateString("es-CO") }}</td>
                <td>{{ e.category }}</td>
                <td>{{ e.description || "—" }}</td>
                <td>{{ e.provider_name || "—" }}</td>
                <td>{{ formatCOP(e.amount) }}</td>
                <td><button class="btn btn-sm btn-outline-danger" @click="eliminarEgreso(e)"><i class="bi bi-trash"></i></button></td>
              </tr>
              <tr v-if="egresos.length === 0">
                <td colspan="6" class="text-center text-muted py-4">Sin egresos registrados.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- MODAL NUEVO EGRESO -->
    <div v-if="showModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5>Nuevo egreso</h5>
            <button class="btn-close" @click="showModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-2">
              <label>Categoría</label>
              <select v-model="form.category" class="form-control">
                <option value="">Seleccionar...</option>
                <option v-for="c in CATEGORIAS" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            <div class="mb-2">
              <label>Monto</label>
              <argon-input v-model="form.amount" type="number" />
            </div>
            <div class="mb-2">
              <label>Fecha</label>
              <input v-model="form.expenseDate" type="date" class="form-control" />
            </div>
            <div class="mb-2">
              <label>Descripción</label>
              <textarea v-model="form.description" class="form-control" rows="2"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardando" @click="guardarEgreso">Guardar</argon-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showModal" class="modal-backdrop fade show"></div>

    <!-- MODAL NUEVO INGRESO -->
    <div v-if="showModalIngreso" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5>Nuevo ingreso</h5>
            <button class="btn-close" @click="showModalIngreso = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-2">
              <label>Categoría</label>
              <select v-model="formIngreso.category" class="form-control">
                <option value="">Seleccionar...</option>
                <option v-for="c in CATEGORIAS_INGRESO" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            <div class="mb-2">
              <label>Monto</label>
              <argon-input v-model="formIngreso.amount" type="number" />
            </div>
            <div class="mb-2">
              <label>Fecha</label>
              <input v-model="formIngreso.incomeDate" type="date" class="form-control" />
            </div>
            <div class="mb-2">
              <label>Descripción</label>
              <textarea v-model="formIngreso.description" class="form-control" rows="2"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showModalIngreso = false">Cancelar</button>
            <argon-button color="success" :disabled="guardandoIngreso" @click="guardarIngreso">Guardar</argon-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showModalIngreso" class="modal-backdrop fade show"></div>

  </div>
</template>
